/**
 * 浮动容器组件
 * 
 * 该组件主要用于在页面中显示一些浮动元素，包括：
 * 1. 返回顶部按钮
 * 2. 反馈按钮
 * 3. 登录引导
 * 4. 发帖按钮
 * 
 * 组件会根据不同的路由页面显示不同的浮动元素，并且支持响应式布局。
 * 在移动端会处理滚动时的显示/隐藏动画效果。
 */

import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import Router, { useRouter } from 'next/router';
import { StoreContext, namespace } from '@/context';
import { pagePathUrl, homeRouterPageName, notShowFeedBack, displayBackToTopRouters } from '@/constants/url';
import { BackToTop } from './BackToTop';
import { Feedback } from './Feedback';
import { LoginGuide } from './LoginGuide';
import { PromiseState } from '@/constants';
import { isRC, isMobile } from '@/utils';
import Styles from './container.module.scss';
import errorCode from '@/constants/errorCode';
import Toast from '@/components/Toast';
import { useTranslations } from 'next-intl';

export interface FloatContainerProps {
  /** 是否启用响应式定位，默认关闭 */
  responsive?: boolean;
}

export default function FloatContainer(props: FloatContainerProps) {
  const { state } = useContext(StoreContext);

  const t = useTranslations();

  const { responsive = false } = props;

  const router = useRouter();
  const { pathname } = router;
  /* 由于设备圈的数据结构不同，单独处理 */
  const slug = (router.query?.slug || '') as string;
  /* 设备圈的系列参数 */
  const series = router.query?.series;
  const seriesValue = (Array.isArray(series) ? series?.[0] : series) || '';

  /* 根据路由页面确定状态命名空间 */
  let stateNamespace = namespace.home;
  switch (pathname) {
    /* 首页 */
    case homeRouterPageName.home:
      stateNamespace = namespace.home;
      break;
    /* 广场 */
    case homeRouterPageName.square:
      stateNamespace = namespace.square;
      break;
    /* 咨询 */
    case homeRouterPageName.consult:
      stateNamespace = namespace.consult;
      break;
    /* 活动 */
    case homeRouterPageName.activity:
      stateNamespace = namespace.activity;
      break;
    /* 设备圈 */
    case homeRouterPageName.devicesSeries:
      stateNamespace = namespace.devices;
      break;
    /* 设备主题 */
    case homeRouterPageName.device:
      stateNamespace = namespace.device;
      break;
    /* 用户名昵称修改 */
    case homeRouterPageName.profile:
      stateNamespace = namespace.profile;
      break;
    default:
      stateNamespace = namespace.home;
  }

  const {
    channelList: cacheChannelList,
    channelActiveKeys,
    devicesSeriesName,
    deviceName,
  } = ([namespace.devices, namespace.device].includes(stateNamespace)
    ? (state[stateNamespace] as any)?.[stateNamespace === namespace.devices ? seriesValue : slug]
    : state[stateNamespace]) || {};

  const { account, sensors, permission } = state[namespace.app];
  const { track, TRACK_EVENTS, getChannelNameBySlug } = sensors || {};

  /* 是否显示浮动容器 */
  const [isVisible, setIsVisible] = useState<boolean>(true);
  /* 定时器 */
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  /* 是否开始 */
  const [isStart, setIsStart] = useState<boolean>(true);
  /* 频道tab定时器 */
  const tabTimer = useRef<number | NodeJS.Timeout | null>(null);
  /* 除了手动滚动时开启动画，其他时候都上锁,禁止动画 */
  const lock = useRef<boolean>(true);
  /* 页面刚打开时和页面关闭时会自动滚动,此时阻止开启动画锁 */
  const isInit = useRef<boolean>(true);

  const isLoggin = useMemo(() => {
    const { id, uuid, status } = account || {};
    if (status !== PromiseState.PENDING && id && uuid) {
      return true;
    }
    return false;
  }, [account]);

  /* 是否是首页 */
  const isHomePage = useMemo(() => {
    return Object.values(homeRouterPageName).some((route) => {
      return route === pathname;
    });
  }, [pathname]);

  /* 是否不显示反馈按钮 */
  const isNotShowFeedBack = useMemo(() => {
    return Object.values(notShowFeedBack).some((route) => {
      return route === pathname;
    });
  }, [pathname]);

  /* 处理滚动事件 */
  useEffect(() => {
    const handleScroll = () => {
      if (!isInit.current) {
        lock.current = false;
      }
      if (timer) {
        clearTimeout(timer);
      }
      if (pathname === '/detail') {
        // pc端详情页不用隐藏
        return;
      }
      setIsVisible(false);
      const newTimer = setTimeout(() => {
        setIsVisible(true);
        lock.current = true;
      }, 500);
      setTimer(newTimer);
    }

    const ele = document.querySelector('#__next');
    ele?.addEventListener('scroll', handleScroll);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      ele?.removeEventListener('scroll', handleScroll);
    };
  }, [timer]);

  useEffect(() => {
    isInit.current = true;
    setTimeout(() => {
      isInit.current = false;
    }, 600)
  }, [pathname])

  useEffect(() => {
    const start = (url: string) => {
      if (url.includes('/pro/detail')) {
        // 进详情页会出现loading,导致屏幕滚动,出现动画
        isInit.current = true;
        setTimeout(() => {
          isInit.current = false;
        }, 600)
      }
    };
    Router.events.on('routeChangeStart', start);
    setTimeout(() => {
      setIsStart(false);
    }, 3000)
    return () => {
      Router.events.off('routeChangeStart', start);
    };
  }, []);

  useEffect(() => {
    // 切换频道tab也会导致屏幕滚动,出现动画
    isInit.current = true;
    if (tabTimer.current) {
      clearTimeout(tabTimer.current);
    }
    tabTimer.current = setTimeout(() => {
      isInit.current = false;
    }, 300)
  }, [channelActiveKeys])

  const handleGotoEdit = () => {
    const trackOption = {
      list_name: getChannelNameBySlug?.(cacheChannelList, channelActiveKeys?.[0]),
      second_list_name: getChannelNameBySlug?.(cacheChannelList, channelActiveKeys?.[1]),
      product_series_list_name: devicesSeriesName ? devicesSeriesName : null,
      product_name_list_name: deviceName ? deviceName : null,
    }

    if (permission?.status !== PromiseState.PENDING) {
      if (permission?.code === errorCode.UNBIND_PHONE) {
        // 未绑定手机号
        Toast.show({
          content: permission?.message || t('phone_number_is_not_bound_tip'),
        });
        setTimeout(() => {
          window.location.href = `${process.env.SITE_ORIGIN}${pagePathUrl.edit}`;
        }, 1000)
        track?.(TRACK_EVENTS?.[namespace.home].uploadButtonsClick.name, {
          ...trackOption,
          is_success: false,
          fail_reason: '未绑定手机号',
        }, namespace.home);
      } else {
        // 成功/未登录
        window.location.href = `${process.env.SITE_ORIGIN}${pagePathUrl.edit}`;
        track?.(TRACK_EVENTS?.[namespace.home].uploadButtonsClick.name, {
          ...trackOption,
          is_success: isLoggin,
          fail_reason: isLoggin ? '' : '未登录',
        }, namespace.home);
      }
    } else {
      // pending
      track?.(TRACK_EVENTS?.[namespace.home].uploadButtonsClick.name, {
        ...trackOption,
        is_success: false,
        fail_reason: '权限校验暂未通过',
      }, namespace.home);
    }
  }

  return (
    <div className={Styles.container}>
      <div className={cn(
        Styles['pc-float'],
        { [Styles['pc-not-home-float']]: !isHomePage },
        { [Styles['pc-detail-float']]: !pathname.includes('mIndex') },
        (!lock.current && isMobile) ? (isVisible ? Styles.fadeIn : Styles.fadeOut) : '',
        responsive && Styles.responsive
      )}>
        {(isHomePage || displayBackToTopRouters.some(i => pathname.includes(i))) && (
          <BackToTop className={Styles.round} shownOffset={360} />
        )}
        {!isNotShowFeedBack && !isRC && <Feedback />}
        {isHomePage && (
          <div className={Styles.post} onClick={handleGotoEdit}>
            <div className={cn(Styles.round, Styles['new-post'])} />
          </div>
        )}
      </div>
      {isHomePage && <LoginGuide className={Styles['pc-login']} stateNamespace={stateNamespace} />}
    </div>
  );
}
